import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Container, Paper, Box, Typography } from '@mui/material';
import CouponRedemption from './CouponRedemption';
import type { CouponData } from '../../../types/coupon';

/**
 * Local CouponData interface for the redemption page.
 * Like CouponLanding, this includes the 'branding' field which is transformed 
 * from 'customBranding' by the API's mergeBrandingSettings function.
 */
interface RedemptionCouponData extends CouponData {
    customBranding?: {
        enabled: boolean;
        company?: {
            logo: {
                url: string;
                width: number;
            };
            name: string;
            websiteUrl: string;
            socialLinks: {
                [key: string]: string;
            };
        };
        theme?: {
            primaryColor: string;
            secondaryColor: string;
            backgroundColor: string;
            textColor: string;
            fontFamily: string;
        };
        landingPage?: {
            welcomeHeading: string;
            welcomeMessage: string;
            buttonText: string;
            footerText: string;
        };
    };
    branding?: {
        theme: {
            primaryColor: string;
            secondaryColor: string;
            backgroundColor: string;
            textColor: string;
            fontFamily: string;
        };
        company: {
            logo: {
                url: string;
                width: number;
            };
            name: string;
            websiteUrl: string;
            socialLinks: {
                [key: string]: string;
            };
        };
        landingPage: {
            welcomeHeading: string;
            welcomeMessage: string;
            defaultWelcomeHeading: string;
            defaultWelcomeMessage: string;
            defaultButtonText: string;
            defaultFooterText: string;
        };
    };
}

const CouponRedemptionContainer: React.FC = () => {
    const { couponId, email, subscriberId } = useParams<{ 
        couponId: string;
        email: string;
        subscriberId: string;
    }>();
    const [theme, setTheme] = useState(createTheme());

    const updateTheme = useCallback((coupon: RedemptionCouponData) => {
        if (!coupon.branding?.theme) return;

        setTheme(createTheme({
            palette: {
                primary: { main: coupon.branding.theme.primaryColor || '#1976d2' },
                secondary: { main: coupon.branding.theme.secondaryColor || '#dc004e' },
                background: {
                    default: coupon.branding.theme.backgroundColor || '#ffffff',
                    paper: coupon.branding.theme.backgroundColor || '#ffffff'
                },
                text: { primary: coupon.branding.theme.textColor || '#000000' }
            },
            typography: {
                fontFamily: coupon.branding.theme.fontFamily || 'Roboto'
            }
        }));
    }, []);

    return (
        <ThemeProvider theme={theme}>
            <Box 
                sx={{ 
                    minHeight: '100vh',
                    backgroundColor: theme.palette.background.default
                }}
            >
                <Box 
                    sx={{
                        py: { xs: 3, md: 4 },
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Container maxWidth="md" sx={{ mb: { xs: 3, md: 4 }, flexGrow: 1 }}>
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: 3, 
                                borderRadius: 0,
                                backgroundColor: theme.palette.background.default,
                                color: theme.palette.text.primary,
                                maxWidth: 'max-content',
                                mx: 'auto',
                                '& > *:not(:last-child)': {
                                    marginBottom: 3
                                },
                                padding:0
                            }}
                        >
                            <CouponRedemption onCouponLoad={updateTheme} />
                        </Paper>
                    </Container>
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default CouponRedemptionContainer;
